import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import get from 'lodash.get';
import saveAs from 'file-saver';
import moment from 'moment';
import Papa from 'papaparse';
import { selectors as authSelectors } from 'store/auth';
import {
  Backdrop,
  Box,
  Card,
  Chip,
  DotProgress,
  FlexBox,
  FormDialog,
  Grid,
  Icon,
  IconButton,
  MuiBox,
  MuiTable,
  MuiTheme,
  SelectDropdown,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  color
} from '@kis-technologies/kis-ui';
import { FiltersOverlay } from './components/FiltersOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { SFOTSalesReportSettings, selectors as ciSelectors, actions as ciActions } from 'store/clientIntegrations';
import { selectors as deliveryLogSelectors, actions as deliveryLogActions, Transaction } from 'store/deliveryLog';
import { useStyles } from './styled';
import { ReconcilationOverlay } from './components/ReconcilationOverlay';
import { getSortedList } from 'utils/stringFormat';
import { RefundOverlay } from './components/RefundOverlay';
import { SettlementReportOverlay } from './components/SettlementReportOverlay';
import { SalesReportOverlay } from './components/SalesReportOverlay';
import { OrderSummaryOverlay } from './components/OrderSummaryOverlay';
import { SFOTSalesReportOverlay } from './components/SFOTSalesReportOverlay';

const DeliveryLogPage: React.FC = () => {
  // ACTIONS AND SELECTORS
  const {
    getDeliveryLog,
    resetDeliveryLog,
    resetExportData,
    getTransactionPriceTypes,
    refundTransaction,
    voidTransaction,
    reconcileTransactions
  } = useActions({
    getDeliveryLog: deliveryLogActions.getDeliveryLog,
    resetDeliveryLog: deliveryLogActions.resetDeliveryLog,
    resetExportData: deliveryLogActions.resetExportData,
    getTransactionPriceTypes: deliveryLogActions.getTransactionPriceTypes,
    refundTransaction: deliveryLogActions.refundTransaction,
    voidTransaction: deliveryLogActions.voidTransaction,
    reconcileTransactions: deliveryLogActions.reconcileTransactions
  });
  const admin = useSelector(authSelectors.selectAdmin);

  const client = useSelector(clientsSelectors.selectClient) as Client;
  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);
  const { data, pending, hasMore, totalRecords } = useSelector(deliveryLogSelectors.selectDeliveryLog);
  const exportData = useSelector(deliveryLogSelectors.selectExportData);
  const orderSummaryData = useSelector(deliveryLogSelectors.selectPriceTypes);
  const exportPending = useSelector(deliveryLogSelectors.selectExportPending);

  const [getClientIntegrations] = useActions([ciActions.getClientIntegrations]);

  const [showFiltersOverlay, setShowFiltersOverlay] = useState(false);
  const [showReconcilationOverlay, setShowReconcilationOverlay] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [sortColumn, setSortColumn] = useState('orderTime');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [flowsToFilter, setFlowsToFilter] = useState<string[]>([]);
  const [deliveryTypesToFilter, setDeliveryTypesToFilter] = useState<string[]>([]);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [filterChips, setFilterChips] = useState<string[]>([]);
  const [chipClosed, setChipClosed] = useState(false);
  const [filter, setFilter] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [environmentId, setEnviornmentId] = useState<string | null>(null);
  const [isRefundOpen, setIsRefundOpen] = useState(false);
  const [selectedRefundTx, setSelectedRefundTx] = useState<string | null>(null);
  const [selectedVoidTx, setSelectedVoidTx] = useState<string | null>(null);
  const [showSettlementReportOverlay, setShowSettlementReportOverlay] = useState(false);
  const [showSalesReportOverlay, setShowSalesReportOverlay] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [showVoid, setShowVoid] = useState(false);

  const classes = useStyles()();

  const sortedEnvironments = getSortedList(clientIntegrations, 'name');
  const selectedEnvironment = clientIntegrations?.find((ci) => ci.id === environmentId);
  const isKTCIEnvironment = selectedEnvironment?.integration?.code === 'kistechnologies';

  const allowRefunds = get(selectedEnvironment, 'configuration.paymentSettings.allowRefunds', false);
  const sfotSalesReportSettings: SFOTSalesReportSettings = get(
    selectedEnvironment,
    'configuration.sfotSalesReportSettings'
  );

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 1:
        return 'Valid';
      case 2:
        return 'Refunded';
      case 3:
        return 'Voided';
      default:
        return '';
    }
  };

  const hasValue = (val: number | null | undefined): val is number => {
    return val !== null && val !== undefined;
  };

  // ON FETCH TRANSACTIONS SUCCESS
  useEffect(() => {
    if (environmentId && !pending && data.length <= 0) {
      setErrorMessage('There is no data to display.');
    }
  }, [data, pending]);

  useEffect(() => {
    if (sortedEnvironments.length > 0 && !environmentId) {
      setEnviornmentId(sortedEnvironments[0].id);
    }
  }, [sortedEnvironments, environmentId]);

  useEffect(() => {
    if (chipClosed) {
      handleFilter();
      setChipClosed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedEndDate, flowsToFilter, deliveryTypesToFilter]);

  // ON UPDATE OF CLIENT
  useEffect(() => {
    // RESET DELIVERY LOG ON CLIENT CHANGE
    resetDeliveryLog();
    resetFilters();
    setPage(1);
    // IF CLIENT EXIST OR SUCCESSFUL ACTION - GET DELIVERY LOGS
    if (client && environmentId) {
      getDeliveryLog({ page: 1, clientIntegrationId: environmentId });
    }
    getClientIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, environmentId]);

  // FETCH DATA FOR THE NEXT PAGE
  useEffect(() => {
    if (page > 1) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let payload: any = {
        page,
        sort: sortDirection,
        clientIntegrationId: environmentId,
        sortColumn
      };
      if (searching) {
        payload = {
          ...payload,
          searchValue: searchTerm.trim()
        };
      } else if (filter) {
        const appliedFilters = getAppliedFilters();
        payload = { ...payload, ...appliedFilters };
      }

      getDeliveryLog(payload);

      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ON EXPORT DATA FETCHED: WRITE TO CSV
  useEffect(() => {
    if (exportData && exportData.length > 0) {
      const formattedData = exportData.map((t: Transaction) => ({
        'Order Time': format(new Date(t.createdAt), 'M/d/yy HH:mm'),
        'Order Number': t.integrationOrderId,
        'Venue Name': t.venueName ?? '',
        'Area Name': t.areaName ?? '',
        'Kiosk Name': t.kioskName ?? '',
        Amount: hasValue(t.amount) ? formatAmount(t.amount) : '',
        Tickets: t.ticketCount ?? '',
        'Delivery Failed Tickets': t.deliveryFailedTicketCount ?? '',
        Flow: t.flow ?? '',
        Type: t.deliveryType ?? '',
        Status: getStatusLabel(t.status),
        'Cardholder Name': t.cardholderName ?? '',
        'Card Type': t.cardType ?? '',
        'Card Bin': t.cardBin ?? '',
        'Card Last Four': t.cardLastFour ?? '',
        'Card Expiry': t.cardExpiry ?? '',
        'Payment Gateway Name': t.paymentGatewayCode ?? '',
        'Payment Gateway Approved Amount': hasValue(t.paymentGatewayApprovedAmount)
          ? formatAmount(t.paymentGatewayApprovedAmount)
          : '',
        'Payment Gateway Sale Reference Number': t.paymentGatewaySaleReferenceNumber ?? '',
        'Payment Gateway Refund Reference Number': t.paymentGatewayRefundReferenceNumber ?? '',
        'Reversed Time': t.reversedAt ? format(new Date(t.reversedAt), 'M/d/yy HH:mm') : '',
        'Transaction Fee Amount': hasValue(t.transactionFee1Amount) ? formatAmount(t.transactionFee1Amount) : '',
        'Transaction Fee Percentage Amount': t.transactionFee1PercentageAmount ?? '',
        'Venue Tax Label': t.venueTax1Label ?? '',
        'Venue Tax Percentage Amount': t.venueTax1PercentageAmount ?? ''
      }));

      const fileName = `DeliveryLog_${selectedEnvironment?.name}_${moment().format('YYYYMMDDHHmmss')}.csv`;

      downloadContentAsCSV(formattedData, fileName);

      resetExportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  // ***************
  // ** FUNCTIONS **
  // ***************
  const globalAdmin = admin?.role === 'GlobalAdmin';
  const isAdmin = admin?.role === 'ClientAdmin';
  const canEditAdminAndGA = isAdmin || globalAdmin;

  /** Convert content and download as CSV */
  const downloadContentAsCSV = (content: (string | number)[][] | object[], fileName: string) => {
    const csv = Papa.unparse(content);
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, fileName);
  };

  const toggleRow = (id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };
  const handleRefund = (transactionId: string) => {
    setSelectedRefundTx(transactionId);
    setIsRefundOpen(true);
  };

  const handleOrderSummary = (transactionId: string) => {
    const selectedTx = data.find((tx) => tx.id === transactionId);
    setTaxAmount(Number(selectedTx?.venueTax1PercentageAmount || 0));

    getTransactionPriceTypes({
      transactionId,
      clientIntegrationId: environmentId
    });
    setShowOrderSummary(true);
  };

  const handleVoid = (transactionId: string) => {
    setSelectedVoidTx(transactionId);
    setShowVoid(true);
  };

  const formatDateTime = (dateTime: string): string =>
    dateTime ? format(new Date(dateTime), 'MM/dd/yyyy hh:mm:ss a') : '';

  const formatAmount = (amount: number | string): string => `$${Number(amount).toFixed(2)}`;

  // RETURN ALL ACTIVE FILTERS FOR FETCH PAYLOAD
  const getAppliedFilters = () => {
    return {
      startTime: selectedStartDate && moment(selectedStartDate).utc().format(),
      endTime: selectedEndDate && moment(selectedEndDate).utc().format(),
      flows: flowsToFilter,
      deliveryTypes: deliveryTypesToFilter
    };
  };

  // RESETS FILTERS
  const resetFilters = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setFlowsToFilter([]);
    setDeliveryTypesToFilter([]);
    setFilterChips([]);
  };

  // INCREMENT PAGE TO TRIGGER NEXT FETCH
  const loadMore = () => {
    const deliveryLogTable: HTMLElement = document.getElementById('deliveryLogTable') || new HTMLElement();
    if (
      deliveryLogTable.offsetHeight < deliveryLogTable.scrollHeight &&
      deliveryLogTable.offsetHeight + deliveryLogTable.scrollTop >= deliveryLogTable.scrollHeight - 1
    ) {
      if (totalRecords >= 100 && hasMore && !pending && !isLoading) {
        setIsLoading(true);
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  // SORT BY SELECTED HEADER AND FETCH
  const handleHeaderClick = (key: string) => {
    let newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    if (sortColumn !== key) {
      newSortDirection = 'asc';
      setSortColumn(key);
    }
    setSortDirection(newSortDirection);
    setPage(1);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let payload: any = {
      page: 1,
      sort: newSortDirection,
      clientIntegrationId: environmentId,
      sortColumn: key
    };
    if (searching) {
      payload = {
        ...payload,
        searchValue: searchTerm.trim()
      };
    } else if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }

    getDeliveryLog(payload);
  };

  // SET SEARCH VALUE AND FETCH; RESET FILTERS
  const handleSearch = (searchVal: string) => {
    resetFilters();
    setSearching(true);
    setFilter(false);
    setPage(1);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      page: 1,
      sort: sortDirection,
      clientIntegrationId: environmentId,
      searchValue: searchVal.trim(),
      sortColumn
    };
    getDeliveryLog(payload);
  };

  const handleApply = (payload: { fromDate: string | null; toDate: string | null; skipResolve: boolean }) => {
    if (!environmentId) return;

    reconcileTransactions({
      clientIntegrationId: environmentId,
      startTime: payload.fromDate!,
      endTime: payload.toDate!,
      identifyOnly: payload.skipResolve
    });
  };

  // APPLY FILTERS AND FETCH; RESET SEARCH
  const handleFilter = () => {
    setFilter(true);
    setSearchTerm('');
    setSearching(false);
    setPage(1);

    const appliedFilters = getAppliedFilters();
    getDeliveryLog({
      page: 1,
      sort: sortDirection,
      clientIntegrationId: environmentId,
      sortColumn,
      ...appliedFilters
    });
  };

  // RESET SEARCH AND FETCH
  const handleClear = () => {
    setSearchTerm('');
    setSearching(false);
    setPage(1);

    getDeliveryLog({
      page: 1,
      sort: sortDirection,
      clientIntegrationId: environmentId,
      sortColumn
    });
  };

  // REMOVE AN ACTIVE FILTER
  const handleChipClose = (val: string) => {
    if (val === 'Flow') {
      setFlowsToFilter([]);
    } else if (val === 'Delivery Type') {
      setDeliveryTypesToFilter([]);
    } else if (val === 'Start Date Time') {
      setSelectedStartDate(null);
    } else if (val === 'End Date Time') {
      setSelectedEndDate(null);
    }

    const newChips = filterChips && filterChips.filter((chip: string) => chip !== val);
    setFilterChips(newChips);
  };

  // EXPORT TO CSV BUTTON CLICK
  const onExportButtonClick = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let payload: any = {
      page: 1,
      sort: sortDirection,
      sortColumn,
      clientIntegrationId: environmentId,
      exportData: true
    };

    if (searching) {
      payload = {
        ...payload,
        searchValue: searchTerm.trim()
      };
    } else if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }

    getDeliveryLog(payload);
  };

  const SortArrow = () => {
    return (
      <Box sx={{ display: 'inline-block', cursor: 'pointer', backgroundColor: 'inherit' }}>
        <SvgIcon name={sortDirection === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
      </Box>
    );
  };

  const isSearchValueEmpty = searchTerm.length === 0;

  const getStatusConfig = (transaction: Transaction) => {
    const baseStyle = {
      width: '5rem',
      height: '1.55rem',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'none',
      '&:hover': {
        backgroundColor: 'inherit',
        boxShadow: 'none'
      }
    };

    switch (transaction.status) {
      case 2: // REFUNDED
        return {
          label: 'Refunded',
          tooltip: `Refunded at ${formatDateTime(transaction.reversedAt || '')}`,
          sx: {
            ...baseStyle,
            color: '#fff',
            backgroundColor: '#f86b67',
            borderRadius: '50px'
          }
        };

      case 3: // VOIDED
        return {
          label: 'Voided',
          tooltip: `Voided at ${formatDateTime(transaction.reversedAt || '')}`,
          sx: {
            ...baseStyle,
            color: '#fff',
            backgroundColor: '#f86b67',
            borderRadius: '50px'
          }
        };

      case 1: // VALID
        return {
          label: 'Valid',
          tooltip: '',
          sx: {
            ...baseStyle,
            color: '#38b549',
            border: '1px solid #38b549',
            backgroundColor: '#fff',
            borderRadius: '50px'
          }
        };

      default:
        return {
          label: 'UNKNOWN',
          tooltip: '',
          sx: baseStyle
        };
    }
  };

  return (
    <MuiBox display="flex" flexDirection="column" height="100%" paddingBottom="24px">
      {(pending || isLoading) && data.length <= 0 && (
        <Backdrop open={(pending || isLoading) && data.length <= 0 ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      {/* ERROR OVERLAY */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open={!!errorMessage}
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
      {/* HEADER ROW */}
      <Grid className={classes.deliverylogHeading}>
        <Box flexOverride="none">
          <Typography className={classes.deliverylogText}>Delivery Log</Typography>
        </Box>
        <Grid className={classes.deliveryRefreshEventButton}>
          <FlexBox direction="column" align="end">
            <MuiBox display="flex" gap="1rem" alignItems="flex-end">
              <Typography className={classes.formLabel}>Environment</Typography>
              <SelectDropdown
                placeholder=""
                items={sortedEnvironments}
                itemToString={(item) => item?.name || ''}
                selectedItem={sortedEnvironments.find((i) => i.id === environmentId) ?? null}
                onChange={(item) => setEnviornmentId(item?.id ?? '')}
                radius="round"
                width="262px"
                disabled={!sortedEnvironments.length}
                sx={{
                  boxShadow: 'none',
                  height: '45px',
                  '& .MuiSelect-select': {
                    color: environmentId ? '#000' : '#9E9E9E'
                  }
                }}
              />
            </MuiBox>
          </FlexBox>
        </Grid>
      </Grid>
      <Grid
        className={classes.deliverylogOptions}
        style={{ paddingBottom: filterChips && filterChips.length === 0 ? '24px' : '0px' }}
      >
        <MuiBox display="flex" gap="10px">
          <div className={classes.root}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              placeholder={
                isKTCIEnvironment ? 'Name,order number,first 6 or last 4 digits of card,card type' : 'Order number'
              }
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value.length === 0) handleClear();
              }}
              InputProps={{
                classes: {
                  root: classes.inputRoot
                },
                endAdornment: searchTerm.length > 0 && (
                  <IconButton onClick={handleClear}>
                    <Icon name="Clear" />
                  </IconButton>
                )
              }}
            />
            <Tooltip title="Search" placement="top" arrow verticalOffset={-8}>
              <IconButton
                disabled={isSearchValueEmpty}
                onClick={() => handleSearch(searchTerm)}
                style={{
                  backgroundColor: isSearchValueEmpty ? '' : '#F46D2E',
                  color: isSearchValueEmpty ? 'grey' : 'white'
                }}
              >
                <Icon name="Search" />
              </IconButton>
            </Tooltip>
          </div>
          <MuiBox>
            <Tooltip title="Filters" placement="top" arrow verticalOffset={-8}>
              <FlexBox direction="column">
                <IconButton
                  onClick={() => setShowFiltersOverlay(true)}
                  data-testid="filter-button"
                  style={{
                    backgroundColor: '#F46D2E',
                    color: 'white'
                  }}
                >
                  <Icon name="FilterAltOutlined" />
                </IconButton>
              </FlexBox>
            </Tooltip>
          </MuiBox>
        </MuiBox>
        <div className={classes.buttonsWarpStyle}>
          <MuiBox
            display="flex"
            sx={{
              gap: '10px'
            }}
            justifyContent="right"
            width="100%"
          >
            {isKTCIEnvironment && canEditAdminAndGA && (
              <Tooltip title="Reconcilation" placement="top" arrow verticalOffset={-8}>
                <Grid>
                  <FlexBox direction="column">
                    <IconButton
                      onClick={() => {
                        setShowReconcilationOverlay(true);
                      }}
                      data-testid="filter-button"
                      style={{
                        backgroundColor: '#F46D2E',
                        color: 'white'
                      }}
                    >
                      <Icon name="CurrencyExchange" />
                    </IconButton>
                  </FlexBox>
                </Grid>
              </Tooltip>
            )}
            <Tooltip title="Sales Report" placement="top" arrow verticalOffset={-8}>
              <Grid>
                <FlexBox direction="column">
                  <IconButton
                    onClick={() => {
                      setShowSalesReportOverlay(true);
                    }}
                    style={{
                      backgroundColor: '#F46D2E',
                      color: 'white'
                    }}
                    data-testid="filter-button"
                  >
                    <Icon name="RequestQuote" />
                  </IconButton>
                </FlexBox>
              </Grid>
            </Tooltip>
            {isKTCIEnvironment && (
              <Tooltip title="Settlement Report" placement="top" arrow verticalOffset={-8}>
                <Grid>
                  <FlexBox direction="column">
                    <IconButton
                      onClick={() => {
                        setShowSettlementReportOverlay(true);
                      }}
                      data-testid="filter-button"
                      style={{
                        backgroundColor: '#F46D2E',
                        color: 'white'
                      }}
                    >
                      <Icon name="AccountBalance" />
                    </IconButton>
                  </FlexBox>
                </Grid>
              </Tooltip>
            )}
            <Tooltip title="Export to .csv" placement="top" arrow verticalOffset={-8}>
              <Grid>
                <FlexBox direction="column">
                  <IconButton
                    onClick={() => onExportButtonClick()}
                    disabled={data.length <= 0}
                    style={{
                      backgroundColor: '#F46D2E',
                      color: 'white'
                    }}
                    data-testid="filter-button"
                  >
                    {!exportPending ? (
                      <Icon name="FileDownloadOutlined" />
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <DotProgress size={3} />
                      </div>
                    )}
                  </IconButton>
                </FlexBox>
              </Grid>
            </Tooltip>
          </MuiBox>
        </div>
      </Grid>
      {filterChips && filterChips.length > 0 && (
        <Grid className={classes.filterOptions}>
          {filterChips.map((chip: string, index: number) => (
            <Chip
              key={`${index}-${chip}`}
              sx={{
                backgroundColor: '#F46D2E',
                color: (theme: MuiTheme) => `${theme.palette.common.white}`,
                fontWeight: 900,
                marginRight: '8px'
              }}
              className={classes.buttonStyl}
              label={chip}
              onDelete={() => {
                handleChipClose(chip);
                setChipClosed(true);
              }}
              variant="outlined"
            />
          ))}
        </Grid>
      )}
      {/* TABLE OF USERS */}
      <MuiBox className={classes.deliverylogOuterBox}>
        <Card hasBorderRadius raised fullHeight sx={{ flex: '1 0 0', display: 'flex', flexDirection: 'column' }}>
          <MuiTable.TableContainer onScroll={loadMore} id="deliveryLogTable" sx={{ flex: 1, overflowY: 'auto' }}>
            <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
              <MuiTable.TableHead>
                <MuiTable.TableRow>
                  <MuiTable.TableCell sx={{ width: '50px' }} />
                  <MuiTable.TableCell
                    className={classes.deliverylogHeaders}
                    sx={{
                      fontWeight: 600,
                      width: '240px',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => handleHeaderClick('orderTime')}
                  >
                    Order Time
                    {sortColumn === 'orderTime' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.deliverylogHeaders}
                    sx={{ fontWeight: 600, width: '220px' }}
                    onClick={() => handleHeaderClick('orderNumber')}
                  >
                    Order Number
                    {sortColumn === 'orderNumber' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.deliverylogHeaders}
                    sx={{ fontWeight: 600, width: '175px' }}
                    onClick={() => handleHeaderClick('kioskName')}
                  >
                    Kiosk
                    {sortColumn === 'kioskName' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.deliverylogHeaders}
                    sx={{ fontWeight: 600, width: '140px', textAlign: 'center' }}
                    onClick={() => handleHeaderClick('amount')}
                  >
                    Amount
                    {sortColumn === 'amount' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.deliverylogHeaders}
                    sx={{ fontWeight: 600, width: '150px', whiteSpace: 'nowrap', textAlign: 'center' }}
                    onClick={() => handleHeaderClick('ticketCount')}
                  >
                    Tickets
                    {sortColumn === 'ticketCount' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.deliverylogHeaders}
                    sx={{ fontWeight: 600, width: '120px' }}
                    onClick={() => handleHeaderClick('flow')}
                  >
                    Flow
                    {sortColumn === 'flow' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.deliverylogHeaders}
                    sx={{ fontWeight: 600, width: '110px' }}
                    onClick={() => handleHeaderClick('deliveryType')}
                  >
                    Type
                    {sortColumn === 'deliveryType' && <SortArrow />}
                  </MuiTable.TableCell>
                  {isKTCIEnvironment && (
                    <MuiTable.TableCell
                      className={classes.deliverylogHeaders}
                      sx={{ fontWeight: 600, width: '110px', textAlign: 'center' }}
                      onClick={() => handleHeaderClick('status')}
                    >
                      Status
                      {sortColumn === 'status' && <SortArrow />}
                    </MuiTable.TableCell>
                  )}
                  {isKTCIEnvironment && (
                    <MuiTable.TableCell
                      className={classes.deliverylogHeaders}
                      sx={{ textAlign: 'center', marginRight: '5px', whiteSpace: 'nowrap', fontWeight: 600 }}
                    >
                      Actions
                    </MuiTable.TableCell>
                  )}
                </MuiTable.TableRow>
              </MuiTable.TableHead>
              <MuiTable.TableBody className={classes.deliverylogTableBody}>
                {data.map((deliverylogObj: Transaction) => {
                  const statusConfig = getStatusConfig(deliverylogObj);

                  return (
                    <>
                      <MuiTable.TableRow key={deliverylogObj.id} className={classes.deliverylogTableRows}>
                        <MuiTable.TableCell>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(deliverylogObj.id);
                            }}
                            sx={{
                              border: `3px solid ${color.PRIMARY}`,
                              borderRadius: '50%',
                              color: color.PRIMARY,
                              height: '30px',
                              width: '30px'
                            }}
                          >
                            <Icon
                              name={expandedRowId === deliverylogObj.id ? 'KeyboardArrowUp' : 'KeyboardArrowDown'}
                            />
                          </IconButton>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell className={classes.deliverylogTableRows}>
                          <Typography fontSize={16} fontWeight={600}>
                            {formatDateTime(deliverylogObj.createdAt)}
                          </Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell className={classes.deliverylogTableRows}>
                          <Typography fontSize={16}>{deliverylogObj.integrationOrderId}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell className={classes.deliverylogTableRows}>
                          <Typography fontSize={16}>{deliverylogObj.kioskName}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell className={classes.deliverylogTableRows} sx={{ textAlign: 'center' }}>
                          <Typography fontSize={16} fontWeight={600}>
                            {deliverylogObj.amount ? formatAmount(deliverylogObj.amount) : '--'}
                          </Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell className={classes.deliverylogTableRows} sx={{ textAlign: 'center' }}>
                          <FlexBox alignItems="center" justifyContent="center" gap="8px">
                            <Typography fontSize={16}>{deliverylogObj.ticketCount}</Typography>
                            {deliverylogObj.deliveryFailedTicketCount > 0 ? (
                              <Icon name="ErrorOutline" sx={{ color: '#c40b0f', fontSize: 18 }} />
                            ) : (
                              <MuiBox width="18px" />
                            )}
                          </FlexBox>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell className={classes.deliverylogTableRows}>
                          <Typography fontSize={16}>{deliverylogObj.flow}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell className={classes.deliverylogTableRows}>
                          <Typography fontSize={16}>{deliverylogObj.deliveryType}</Typography>
                        </MuiTable.TableCell>
                        {isKTCIEnvironment && (
                          <MuiTable.TableCell sx={{ textAlign: 'center' }}>
                            <Tooltip title={statusConfig.tooltip} placement="top" arrow verticalOffset={-8}>
                              <Box
                                sx={{
                                  ...statusConfig.sx,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'default',
                                  pointerEvents: 'none'
                                }}
                              >
                                {statusConfig.label}
                              </Box>
                            </Tooltip>
                          </MuiTable.TableCell>
                        )}
                        {isKTCIEnvironment && (
                          <MuiTable.TableCell sx={{ textAlign: 'center' }}>
                            <MuiBox
                              sx={{ alignItems: 'center', gap: '10px', display: 'flex', justifyContent: 'center' }}
                            >
                              <Tooltip title="Order Summary" placement="top" arrow verticalOffset={-8}>
                                <IconButton
                                  sx={{
                                    border: '1.5px solid black',
                                    borderRadius: '50%'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOrderSummary(deliverylogObj.id);
                                  }}
                                >
                                  <Icon name="ShoppingBasket" fontSize="medium" sx={{ color: 'black' }} />
                                </IconButton>
                              </Tooltip>
                              {deliverylogObj.status === 1 && canEditAdminAndGA && allowRefunds && (
                                <Tooltip title="Refund Transaction" placement="top" arrow verticalOffset={-8}>
                                  <IconButton
                                    sx={{
                                      border: '1.5px solid black',
                                      borderRadius: '50%'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRefund(deliverylogObj.id);
                                    }}
                                  >
                                    <Icon name="MoneyOffCsredOutlined" fontSize="medium" sx={{ color: 'black' }} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {deliverylogObj.status === 1 && globalAdmin && (
                                <Tooltip title="Void Transaction" placement="top" arrow verticalOffset={-8}>
                                  <IconButton
                                    sx={{
                                      border: '1.5px solid black',
                                      borderRadius: '50%'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleVoid(deliverylogObj.id);
                                    }}
                                  >
                                    <Icon name="Block" sx={{ color: 'black' }} fontSize="medium" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </MuiBox>
                          </MuiTable.TableCell>
                        )}
                      </MuiTable.TableRow>
                      {expandedRowId === deliverylogObj.id && (
                        <MuiTable.TableRow>
                          <MuiTable.TableCell colSpan={10} sx={{ backgroundColor: '#fafafa', paddingLeft: '4rem' }}>
                            <Box pl={6} py={2}>
                              {deliverylogObj.cardholderName &&
                                deliverylogObj.cardType &&
                                deliverylogObj.cardBin &&
                                deliverylogObj.cardLastFour &&
                                deliverylogObj.cardExpiry && (
                                  <Typography fontSize={14}>
                                    {deliverylogObj.cardholderName} · {deliverylogObj.cardType} ·{' '}
                                    {deliverylogObj.cardBin}*****{deliverylogObj.cardLastFour} · Exp{' '}
                                    {deliverylogObj.cardExpiry}
                                  </Typography>
                                )}

                              {(deliverylogObj.paymentGatewayCode ||
                                deliverylogObj.paymentGatewaySaleReferenceNumber) && (
                                <Typography fontSize={14} mt={1}>
                                  {
                                    // eslint-disable-next-line no-nested-ternary
                                    deliverylogObj.paymentGatewayCode === 'payConex'
                                      ? 'PayConex'
                                      : deliverylogObj.paymentGatewayCode === 'payConexPlus'
                                        ? 'PayConex Plus'
                                        : deliverylogObj.paymentGatewayCode
                                  }{' '}
                                  · {deliverylogObj.paymentGatewaySaleReferenceNumber}
                                </Typography>
                              )}

                              <Typography fontSize={14} mt={1}>
                                {deliverylogObj.ticketCount - deliverylogObj.deliveryFailedTicketCount} of{' '}
                                {deliverylogObj.ticketCount} tickets were delivered successfully
                              </Typography>
                            </Box>
                          </MuiTable.TableCell>
                        </MuiTable.TableRow>
                      )}
                    </>
                  );
                })}
              </MuiTable.TableBody>
            </MuiTable.Table>
          </MuiTable.TableContainer>
        </Card>
      </MuiBox>
      <Grid className={classes.footer}>
        <p className={classes.total}>Orders: {totalRecords}</p>
      </Grid>
      <FiltersOverlay
        onClose={() => setShowFiltersOverlay(false)}
        isOpen={showFiltersOverlay}
        flowsToFilter={flowsToFilter}
        setFlowsToFilter={setFlowsToFilter}
        deliveryTypesToFilter={deliveryTypesToFilter}
        setDeliveryTypesToFilter={setDeliveryTypesToFilter}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedStartDate={setSelectedStartDate}
        setSelectedEndDate={setSelectedEndDate}
        filterChips={filterChips}
        setFilterChips={setFilterChips}
        handleFilter={handleFilter}
      />
      <ReconcilationOverlay
        isOpen={showReconcilationOverlay}
        onClose={() => {
          setShowReconcilationOverlay(false);
        }}
        onApply={handleApply}
      />
      {isRefundOpen && (
        <RefundOverlay
          isOpen={isRefundOpen}
          onClose={() => setIsRefundOpen(false)}
          onConfirm={(email: string) => {
            if (selectedRefundTx && environmentId) {
              refundTransaction({
                transactionId: selectedRefundTx,
                clientIntegrationId: environmentId,
                email
              });
            }
            setIsRefundOpen(false);
            setSelectedRefundTx(null);
          }}
        />
      )}
      {/* Settlement Report Overlay */}
      {showSettlementReportOverlay && selectedEnvironment && (
        <SettlementReportOverlay
          clientIntegration={selectedEnvironment}
          downloadContentAsCSV={downloadContentAsCSV}
          isGlobalAdmin={globalAdmin}
          onClose={() => setShowSettlementReportOverlay(false)}
          setErrorMessage={setErrorMessage}
        />
      )}
      {/* Sales Report Overlay */}
      {showSalesReportOverlay && selectedEnvironment && !sfotSalesReportSettings && (
        <SalesReportOverlay
          clientIntegration={selectedEnvironment}
          downloadContentAsCSV={downloadContentAsCSV}
          onClose={() => setShowSalesReportOverlay(false)}
          setErrorMessage={setErrorMessage}
        />
      )}
      {/* SFOT Sales Report Overlay */}
      {showSalesReportOverlay && selectedEnvironment && sfotSalesReportSettings && (
        <SFOTSalesReportOverlay
          clientIntegration={selectedEnvironment}
          onClose={() => setShowSalesReportOverlay(false)}
          setErrorMessage={setErrorMessage}
          sfotSalesReportSettings={sfotSalesReportSettings}
        />
      )}
      <OrderSummaryOverlay
        isOpen={showOrderSummary}
        data={orderSummaryData}
        taxAmount={taxAmount}
        onClose={() => {
          setShowOrderSummary(false);
          setTaxAmount(0);
        }}
      />
      {showVoid && (
        <FormDialog
          open={showVoid}
          confirmButtonAction={() => {
            if (selectedVoidTx && environmentId) {
              voidTransaction({
                transactionId: selectedVoidTx,
                clientIntegrationId: environmentId
              });
              setShowVoid(false);
              setSelectedVoidTx(null);
            }
          }}
          confirmButtonText="Void"
          cancelButtonAction={() => {
            setShowVoid(false);
            setSelectedVoidTx(null);
          }}
          cancelButtonText="Cancel"
        >
          <Typography fontSize={24}>Are you sure you want to void this transaction?</Typography>
        </FormDialog>
      )}
    </MuiBox>
  );
};

export default DeliveryLogPage;
